import { useEffect, useState } from 'react';

import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import {
  getVendorTaxonomyId,
  getVendorType,
} from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { StorefrontCardView } from '@zola/svc-marketplace-ts-types';
import type { WCoupleContextView } from '@zola/svc-web-api-ts-client';

import type { CamelCasedPropertiesDeep } from 'type-fest/index.d';

import { receivedBulkInquiryToStorefront } from '~/actions/types/couplesInquiriesActionTypes';
import { cardViewToVendorCardVendor } from '~/components/common/cards/util/helper';
import { VendorCardVendor } from '~/components/common/cards/util/types';
import { useUserContext } from '~/contexts/UserContext';
import { useAppDispatch } from '~/reducers';
import { MappedGalleryPhotoView, VendorCardView } from '~/types/responseTypes';
import ApiService from '~/util/apiService';
import { StandardLogFn } from '~/util/logger';

import { RecentlyViewedStorefrontsContext } from './RecentlyViewedStorefrontsContext';

type Location = {
  // This should be the preferred way of specifying a location
  vendorMarketId: number;
};

/**
 * Provides recently viewed vendors, either within a category, or across all categories
 * for display on a couples facing page.
 */
export const RecentlyViewedStorefrontsProvider: React.FC<{
  limit?: number;
  includeGallery?: boolean;
  taxonomyKey?: VendorTaxonomyKey;
  location?: Location;
}> = ({ children, taxonomyKey, limit = 10, includeGallery = true, location }) => {
  const userContext = useUserContext();

  const dispatch = useAppDispatch();

  const [ready, setReady] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState<VendorCardVendor[]>([]);
  const [hasFetched, setHasFetched] = useState(false);

  const addGalleryToCards = (vendor: VendorCardView | StorefrontCardView) => {
    const mappedVendor = cardViewToVendorCardVendor(vendor);
    return ApiService.get<MappedGalleryPhotoView[]>(
      `/web-marketplace-api/v1/storefront/${mappedVendor.storefrontUuid}/gallery?limit=4`
    )
      .then((gallery) => {
        return {
          ...mappedVendor,
          imageIds:
            [mappedVendor.storefrontCoverId, ...gallery.map((image) => image.uuid)].filter(
              (item): item is string => !!item
            ) || null,
        };
      })
      .catch(() => {
        return mappedVendor;
      });
  };

  useEffect(() => {
    if (userContext && !hasFetched) {
      setHasFetched(true);
      const apiCall = () => {
        let where = {};
        if (location && location.vendorMarketId) {
          // This is used to determine how the cards are displayed with respect
          // to the market that is being searched: is it the vendors home market,
          // a travel market, etc.
          if (location.vendorMarketId) {
            where = {
              vendor_market_id: location.vendorMarketId,
            };
          }
        }
        const params = {
          inquiryRequest: {
            vendor_type: taxonomyKey ? getVendorType(taxonomyKey) : undefined,
            limit: 500,
          },
          recentlyViewedStorefrontsRequest: {
            limit,
            taxonomy_node_id: taxonomyKey ? getVendorTaxonomyId(taxonomyKey) : undefined,
            ...where,
          },
        };

        return ApiService.post<CamelCasedPropertiesDeep<WCoupleContextView>>(
          `/web-marketplace-api/v1/marketplace-context/for-couple`,
          params
        );
      };

      if (!userContext.is_guest) {
        apiCall()
          .then((response) => {
            const { recentlyViewed: list, storefrontUuidToInquiryUuidMap } = response;

            if (storefrontUuidToInquiryUuidMap) {
              dispatch(receivedBulkInquiryToStorefront(storefrontUuidToInquiryUuidMap));
            }

            if (list && list.length > 0) {
              if (includeGallery) {
                // Need to cast to unknown because svc-web-api types and svc-marketplace types don't match
                return Promise.all((list as unknown as VendorCardView[]).map(addGalleryToCards));
              }
              return (list as unknown as VendorCardView[]).map(cardViewToVendorCardVendor);
            }

            return [];
          })
          .then((mappedCards) => {
            setRecentlyViewed(mappedCards);
          })
          .catch(StandardLogFn)
          .finally(() => {
            setReady(true);
          });
      } else {
        // Don't block any loading for guests
        setReady(true);
      }
    }
  }, [dispatch, includeGallery, limit, location, taxonomyKey, userContext]);

  return (
    <RecentlyViewedStorefrontsContext.Provider value={{ recentlyViewed, ready }}>
      {children}
    </RecentlyViewedStorefrontsContext.Provider>
  );
};
